import type { DemandeRemboursementDTO } from '@/features/api/extranet/dto/Demande/DR/DemandeRemboursementDTO.ts'
import type { FinancementDTO } from '@/features/api/extranet/dto/Demande/shared'
import {
  preDateValide,
  preEquals,
  preNombreValide,
  preNotEquals,
  preNotNullOrEmpty,
  rgEstCommuneFactory,
  rgEstDate,
  rgEstMaxLengthFactory,
  rgEstNumeric,
  rgEstNumericMinFactory,
  rgEstObligatoireFactory,
  rgEstVraiFactory,
} from '@/features/domaine/regles-akto/shared'
import { LevelRegleEnum } from '@/features/domaine/types/opco'
import { addMonth, getToday, isDate, isNullOrUndefined, isNumber } from '@/features/shared/tools'
import { addPreBuilder } from '../../moteur-regles/addPre.ts'
import type { ModelValidateurResult } from '../../moteur-regles/Regle.ts'
import { PropertyStatus } from '../../property-status/PropertyStatus.ts'
import {
  quandEstObligatoireAttestation,
  quandEstObligatoireFrais,
  quandEstPresentAttestation,
  quandEstPresentFrais,
  targetPre,
} from './preconditions'
import type {
  ContextReglesDR,
  ReglesAttestation,
  ReglesDR,
  ReglesFinancement,
  ReglesFrais,
  ReglesMission,
  ReglesModule,
} from './types.ts'

const addPre = addPreBuilder<DemandeRemboursementDTO, ContextReglesDR>()

/* FRAIS ------------------------------------------------------------------------ */
const reglesFrais: ReglesFrais = {
  tauxTva: {
    //...quandEstObligatoireFrais('tauxTva', rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
    ...quandEstPresentFrais('tauxTva', {
      ...rgEstObligatoireFactory(LevelRegleEnum.Technique),
      ...rgEstNumeric,
    }),
  },
  montantDemandeRemboursement: {
    ...quandEstObligatoireFrais('montantDemandeRemboursement', rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
    ...quandEstPresentFrais('montantDemandeRemboursement', {
      ...rgEstNumeric,
      ...rgEstNumericMinFactory(0),
    }),
  },
}

/* ATTESTATIONS ------------------------------------------------------------------- */
const reglesAttestation: ReglesAttestation = {
  dateDebut: {
    ...quandEstObligatoireAttestation('dateDebut', rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
    ...quandEstPresentAttestation('dateDebut', {
      ...rgEstDate,
      superieurEqaleDateDebutModule: {
        pre: (_, model) => isDate(model.dateDebut),
        validateur: (_, parent, model) => {
          const module = model.modules.find((x) => x.idModule === parent.idModule)
          if (isNullOrUndefined(module)) return true

          return module.dateDebut! <= parent.dateDebut!
        },
        message: 'La date de début doit être supérieure ou égale à la date de début du module',
      },
    }),
  },
  dateFin: {
    ...quandEstObligatoireAttestation('dateFin', rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
    ...quandEstPresentAttestation('dateDebut', {
      ...rgEstDate,
      superieurEqaleDateDebut: {
        pre: (_, model) => isDate(model.dateDebut),
        validateur: (_, model) => model.dateDebut! <= model.dateFin!,
        message: 'La date de début doit être inférieure ou égale à la date de fin',
      },
      inferieurEqaleDateFinModule: {
        pre: (_, model) => isDate(model.dateDebut),
        validateur: (_, parent, model) => {
          const module = model.modules.find((x) => x.idModule === parent.idModule)
          if (isNullOrUndefined(module)) return true

          return module.dateFin! >= parent.dateFin!
        },
        message: 'La date de fin doit être inférieure ou égale à la date de fin du module',
      },
      inferieurDateDuJour: {
        pre: [preDateValide],
        validateur: (_, parent, _model) => {
          const dateJour = getToday()

          return dateJour > new Date(parent.dateFin!)
        },
        message: 'La date de fin doit être inférieure à la date du jour',
      },
    }),
  },
  duree: {
    ...quandEstObligatoireAttestation('duree', rgEstObligatoireFactory(LevelRegleEnum.Transmettre)),
    ...quandEstPresentAttestation('duree', {
      ...rgEstNumeric,
      ...rgEstNumericMinFactory(0),
      inferieurEqaleDureeModule: {
        pre: (_, model) => isNumber(model.duree),
        validateur: (_, parent, model) => {
          const module = model.modules.find((x) => x.idModule === parent.idModule)

          if (isNullOrUndefined(module)) return true

          return module.duree! >= parent.duree!
        },
        message: 'La durée doit être inférieure ou égale à la durée du module',
      },
    }),
  },
}

const reglesModule: ReglesModule = {
  $model: {
    'au-moins-un-frais-traité': {
      validateur: (_value, modele) => {
        return {
          '': modele.modules.some((m) => m.estTraite),
        }
      },
      message: 'Au moins un module doit être selectionné',
      level: LevelRegleEnum.Transmettre,
    },
  },
}

function periodeFormationExterne(model: DemandeRemboursementDTO): { dateDebut?: string; dateFin?: string } {
  if (!model.modules) return {}
  return model.modules
    .filter((module) => module.typeModule === 'Externe')
    .reduce(
      (periode, module) => {
        if (!module.dateDebut && !module.dateFin) return periode

        if (isDate(module.dateDebut)) {
          if (!periode.dateDebut || module.dateDebut < periode.dateDebut) {
            periode.dateDebut = module.dateDebut
          }
        }

        if (isDate(module.dateFin)) {
          if (!periode.dateFin || module.dateFin > periode.dateFin) {
            periode.dateFin = module.dateFin
          }
        }

        return periode
      },
      {} as ReturnType<typeof periodeFormationExterne>,
    )
}

/* REGLES  Mission  ------------------------------------------------------------ */
const reglesMission: ReglesMission = {
  avecMission: {
    ...addPre(
      (...[, , , context]) => context.status?.mission?.avecMission?.$status !== PropertyStatus.NotPresent,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  avecTransport: {
    ...addPre(
      (...[, , , context]) => context.status?.mission?.avecTransport?.$status !== PropertyStatus.NotPresent,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  posteOccupe: {
    ...addPre(
      (...[, , , context]) => context.status?.mission?.posteOccupe?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...addPre(
      (...[, , , context]) => context.status?.mission?.posteOccupe?.$status !== PropertyStatus.NotPresent,
      rgEstMaxLengthFactory(100),
    ),
  },
  duree: {
    ...addPre(
      (...[, , , context]) => context.status?.mission?.duree?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...addPre((...[, , , context]) => context.status?.mission?.duree?.$status !== PropertyStatus.NotPresent, {
      ...rgEstNumeric,
      'min-max': {
        pre: [preNotNullOrEmpty, preNombreValide],
        validateur: (value: number, _parent, model) => {
          // cipi
          let min = 70
          if (model.sousDispositif === 'CDPI') {
            const nbHeureModulesHorsPositionnement = model.modules
              ?.filter((x) => x.typeModule !== 'BilanPositionnement')
              .reduce((sum, module) => sum + (module.duree ?? 0), 0)

            min = Math.floor(nbHeureModulesHorsPositionnement / 3)
          }

          if (value < min) return `La durée de mission doit être supérieure ou égale à ${min} heures`

          return
        },
        level: LevelRegleEnum.Metier,
      },
      /*
      'max-cumul-formation': {
        pre: [preNotNullOrEmpty, preNombreValide],
        validateur: (value, _parent, model) => {
          const sommeDuréeFormation =
            model?.formation?.modules
              ?.filter((module) => module.typeModule === 'Externe')
              ?.map((module) => module.duree)
              ?.filter(isNumber)
              ?.reduce((acc, durée) => (acc += durée), 0) ?? 0
          return value! + sommeDuréeFormation <= 315
        },
        message: 'La durée totale de formation externe et de la mission ne doit pas dépasser 315h',
        level: LevelRegleEnum.Metier,
      },
      */
    }),
  },
  dateDebut: {
    ...addPre(
      (...[, , , context]) => context.status?.mission?.dateDebut?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...addPre((...[, , , context]) => context.status?.mission?.dateDebut?.$status !== PropertyStatus.NotPresent, {
      ...rgEstDate,
      'cdpi-date-sup-egale-date-fin-contrat': {
        pre: [preDateValide, targetPre('sousDispositif', preEquals('CDPI'))],
        validateur: (value, _parent, model) => {
          const { dateFin: dateFinFormationExterne } = periodeFormationExterne(model)
          if (!dateFinFormationExterne) return true
          const dateDebut = new Date(value!)
          return dateDebut >= new Date(dateFinFormationExterne)
        },
        message: 'La date de début de délégation doit être postérieure à la date de fin du contrat CDPI.',
        level: LevelRegleEnum.Metier,
      },
    }),
  },
  dateFin: {
    ...addPre(
      (...[, , , context]) => context.status?.mission?.dateFin?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...addPre((...[, , , context]) => context.status?.mission?.dateFin?.$status !== PropertyStatus.NotPresent, {
      ...rgEstDate,
      'date-fin-sup-date-debut': {
        pre: [preDateValide, targetPre('mission.dateDebut', preDateValide)],
        validateur: (value, _parent, model) => {
          return value! > model.mission!.dateDebut!
        },
        message: 'La date de fin de mission doit être après la date de début',
        level: LevelRegleEnum.Metier,
      },
      'cipi-amplitude-maximale': {
        pre: [preDateValide, targetPre('sousDispositif', preEquals('CIPI'))],
        validateur: (value, _parent, model) => {
          const { dateDebut: dateDebutFormationExterne } = periodeFormationExterne(model)
          if (!dateDebutFormationExterne) return true
          const dateFin = new Date(value!)
          return dateFin <= addMonth(new Date(dateDebutFormationExterne), 6)
        },
        message: 'Les dates de mission doivent être comprises dans la période contractuelle du CIPI.',
        level: LevelRegleEnum.Metier,
      },
      'cdpi-date-fin-inf-fin-formation-plus-3-mois': {
        pre: [preDateValide, targetPre('sousDispositif', preEquals('CDPI')), targetPre('mission.avecTransport', preNotEquals(true))],
        validateur: (value, _parent, model) => {
          const { dateFin: dateFinFormationExterne } = periodeFormationExterne(model)
          if (!dateFinFormationExterne) return true
          const dateFin = new Date(value!)
          return dateFin <= addMonth(new Date(dateFinFormationExterne), 3)
        },
        message: 'La date de fin de mission doit être dans les 3 mois suivant la fin de la formation.',
        level: LevelRegleEnum.Metier,
      },
      'cdpi-date-fin-inf-fin-formation-plus-6-mois': {
        pre: [preDateValide, targetPre('sousDispositif', preEquals('CDPI')), targetPre('mission.avecTransport', preEquals(true))],
        validateur: (value, _parent, model) => {
          const { dateFin: dateFinFormationExterne } = periodeFormationExterne(model)
          if (!dateFinFormationExterne) return true
          const dateFin = new Date(value!)
          return dateFin <= addMonth(new Date(dateFinFormationExterne), 6)
        },
        message: 'La date de fin de mission doit être dans les 6 mois suivant la fin de la formation (transport).',
        level: LevelRegleEnum.Metier,
      },
    }),
  },
}

/* REGLES  Financement  -------------------------------------------------------- */
const reglesFinancement: ReglesFinancement = {
  attestationResteChargeContribution: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  },
  modePriseEnChargeReste: {
    ...addPre<FinancementDTO>(
      (...[, , , context]) => context.status?.financement?.modePriseEnChargeReste?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
}

/* REGLES  ------------------------------------------------------------------------ */
export const reglesConfiguration: ReglesDR = {
  frais: reglesFrais,
  attestations: reglesAttestation,
  modules: reglesModule,
  mission: reglesMission,
  financement: reglesFinancement,
  reference: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ...rgEstMaxLengthFactory(30),
  },
  attestationSurHonneur: rgEstVraiFactory(LevelRegleEnum.Transmettre),
  attestationPiecesJustificatives: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.attestationPiecesJustificatives?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  acceptationCg: rgEstVraiFactory(LevelRegleEnum.Transmettre),
  validationNotice: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  lieuSignature: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ...rgEstMaxLengthFactory(50),
    ...rgEstCommuneFactory(LevelRegleEnum.Transmettre),
  },
  commentaire: rgEstMaxLengthFactory(256),
  $model: {
    'attestation-présence-obligatoire': {
      level: LevelRegleEnum.Transmettre,
      message: 'Attestation de présence obligatoire',
      validateur: (model, _globalModel, context) => {
        for (const doc of context.naturesFichiers.obligatoires) {
          if (model.fichiers?.some((x) => x.nature === doc) !== true) return { fichiers: false }
        }
      },
    },
    'frais-obligatoire': {
      level: LevelRegleEnum.Transmettre,
      message: "Aucun frais de remboursement n'a été demandé",
      validateur: (_model, globalModel) => {
        const result: ModelValidateurResult<DemandeRemboursementDTO> = {}
        for (const [index, propMod] of globalModel.modules!.entries()) {
          if (propMod.estTraite === true) {
            const frais = globalModel.frais.filter((x) => x.idModule === propMod.idModule)
            const montantDemande = frais.reduce((acc, frais) => {
              const montant = frais.montantDemandeRemboursement || 0
              return acc + montant
            }, 0)
            if (montantDemande === 0) result[`modules.${index}`] = false
          }
        }

        return result
      },
    },
  },
} as const
